import { useState } from 'react';
import { Loader2, Mail, MoreVertical, Pencil, Plus, Trash2, Users } from 'lucide-react';

import MemberFormDialog from '@/components/account/MemberFormDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAccountMembers } from '@/hooks/use-account-members';
import type { FamilyMember, FamilyMemberPayload } from '@/services/account.service';

const INVITATION_BADGES = {
  pending: { label: 'Convite pendente', className: 'bg-amber-100 text-amber-800 hover:bg-amber-100' },
  expired: { label: 'Convite expirado', className: 'bg-red-100 text-red-700 hover:bg-red-100' },
} as const;

function MemberBadge({ member }: Readonly<{ member: FamilyMember }>) {
  if (member.is_owner) {
    return <Badge>Titular</Badge>;
  }
  if (member.invitation_status) {
    const badge = INVITATION_BADGES[member.invitation_status];
    return <Badge className={badge.className}>{badge.label}</Badge>;
  }
  return <Badge variant="secondary">Membro</Badge>;
}

interface AccountMembersCardProps {
  /** Só consulta a API quando a seção está visível e o plano comporta membros. */
  enabled: boolean;
}

type FormState = { open: false } | { open: true; member: FamilyMember | null };

export default function AccountMembersCard({ enabled }: Readonly<AccountMembersCardProps>) {
  const {
    members,
    canManage,
    maxUsers,
    availableSlots,
    isLoading,
    isSaving,
    inviteMember,
    updateMember,
    resendInvite,
    removeMember,
  } = useAccountMembers(enabled);

  const [formState, setFormState] = useState<FormState>({ open: false });
  const [memberToRemove, setMemberToRemove] = useState<FamilyMember | null>(null);

  const handleSubmit = async (payload: FamilyMemberPayload) => {
    if (!formState.open) return;
    const saved = formState.member
      ? await updateMember(formState.member.id, payload)
      : await inviteMember(payload);
    if (saved) {
      setFormState({ open: false });
    }
  };

  const handleRemove = async () => {
    if (!memberToRemove) return;
    if (await removeMember(memberToRemove)) {
      setMemberToRemove(null);
    }
  };

  const renderMembers = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-8" data-testid="account-members-loading">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </div>
      );
    }

    if (members.length === 0) {
      return <p className="text-sm text-muted-foreground py-2">Nenhum usuário vinculado à conta.</p>;
    }

    return (
      <div className="space-y-3">
        {members.map((member) => (
          <div
            key={member.id}
            data-testid="account-member-row"
            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border border-border/60 px-4 py-3"
          >
            <div className="min-w-0">
              <p className="font-medium text-foreground">{member.name}</p>
              <p className="text-sm text-muted-foreground break-all">{member.email}</p>
              <div className="mt-1">
                <MemberBadge member={member} />
              </div>
            </div>
            {canManage && !member.is_owner && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    aria-label={`Ações de ${member.name}`}
                    title={`Ações de ${member.name}`}
                  >
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {member.invitation_status && (
                    <DropdownMenuItem
                      onClick={() => void resendInvite(member)}
                      disabled={isSaving}
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Reenviar convite
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => setFormState({ open: true, member })}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Editar
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-red-600 focus:text-red-600"
                    onClick={() => setMemberToRemove(member)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Excluir
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card className="border-border/60">
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Usuários
          </CardTitle>
          <CardDescription>
            {canManage
              ? 'Convide pessoas para acessar a sua conta e acompanhar os mesmos dados.'
              : 'Você acessa a conta de outro titular. Somente o titular pode gerenciar usuários.'}
          </CardDescription>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="secondary">
            {members.length}/{maxUsers || members.length}
          </Badge>
          {canManage && (
            <Button
              onClick={() => setFormState({ open: true, member: null })}
              disabled={availableSlots === 0}
            >
              <Plus className="h-4 w-4 mr-2" />
              Convidar usuário
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {canManage && !isLoading && maxUsers > 0 && (
          <p className="text-sm text-muted-foreground">
            Você ainda pode convidar {availableSlots} usuário(s) nesta conta.
          </p>
        )}
        {renderMembers()}
      </CardContent>

      <MemberFormDialog
        open={formState.open}
        member={formState.open ? formState.member : null}
        isSaving={isSaving}
        onOpenChange={(open) => !open && setFormState({ open: false })}
        onSubmit={(payload) => void handleSubmit(payload)}
      />

      <AlertDialog open={!!memberToRemove} onOpenChange={(open) => !open && setMemberToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir usuário da conta?</AlertDialogTitle>
            <AlertDialogDescription>
              {memberToRemove?.invitation_status
                ? `O convite de ${memberToRemove.name} será cancelado e o link enviado deixará de funcionar.`
                : `${memberToRemove?.name ?? 'O usuário'} perderá acesso à sua conta.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSaving}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={(event) => {
                event.preventDefault();
                void handleRemove();
              }}
              disabled={isSaving}
            >
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
